/** TPR Type Slugs */
export const TPRTypeSlug = Object.freeze({
  NATIONAL: "tprType_national",
  ACCOUNT_SPECIFIC: "tprType_account_specific",
});

/** Promotion Stage IDs */
export const StageId = Object.freeze({
  DATA_SYNC: "data_sync",
  SYNC_READY: "sync_ready",
  PORTAL_UPLOAD: "portal_upload",
});

/** Stage State color mapping */
export const StageStateColor = Object.freeze({
  pending: "base",
  active: "primary",
  warning: "warning",
  error: "danger",
  complete: "success",
});

/**
 * Severity levels — including OK, a synthetic roll-up value emitted by the
 * backend for active discounts with no issues (see IssueSeverity.OK on the
 * backend). Issues themselves are never stored with severity=OK.
 */
export const SEVERITY = {
  OK: "OK",
  INACTIVE: "INACTIVE",
  INFO: "INFO",
  WARNING: "WARNING",
  ERROR: "ERROR",
};

/**
 * Discount status buckets — identical to SEVERITY, aliased for the
 * discountCountByStatus top-level metrics where "OK" reads more naturally as
 * a status bucket than as a severity level.
 */
export const DISCOUNT_STATUS = { ...SEVERITY };

/**
 * Stat tile filter keys. Three of them are severity buckets; DISABLED is
 * synthetic (inactive rows carry no severity of their own). The selected key is
 * what lands in Redux as the active filter, so tile identity, count key, and
 * filter value are all the same string — no mapping layer to drift.
 */
export const STAT_TILE_FILTER = Object.freeze({
  OK: SEVERITY.OK,
  WARNING: SEVERITY.WARNING,
  ERROR: SEVERITY.ERROR,
  DISABLED: "disabled",
});

/**
 * The tiles the promotion list shows, in display order. Promotions are never
 * "disabled" the way discounts are — getPromoTileBucket can't return DISABLED —
 * so that tile is omitted here and rejected as a promo filter.
 */
export const PROMO_STAT_TILES = Object.freeze([
  STAT_TILE_FILTER.OK,
  STAT_TILE_FILTER.WARNING,
  STAT_TILE_FILTER.ERROR,
]);

/**
 * Query params the promo list persists its view in, so returning to the list or
 * sharing its URL restores the same sort, filters and search.
 *
 * All four are readable: SORT is a comma-joined column list with a `-` prefix
 * for descending (`sort=-startDate,name`), and FILTER is AG Grid's filter model
 * as JSON. Only params the user has actually changed appear.
 */
export const PROMO_LIST_URL_PARAMS = Object.freeze({
  SEARCH: "q",
  TILE: "tile",
  SORT: "sort",
  FILTER: "filter",
});

/**
 * The grid-owned subset of PROMO_LIST_URL_PARAMS, shaped for the AG Grid URL
 * state helpers (which take `{sort, filter}`).
 */
export const PROMO_LIST_GRID_PARAMS = Object.freeze({
  sort: PROMO_LIST_URL_PARAMS.SORT,
  filter: PROMO_LIST_URL_PARAMS.FILTER,
});

/**
 * Lifecycle of the per-user PPM grid preferences fetch.
 *
 * Requested once per session by PPMPreferencesGate and read by every grid that
 * restores a saved column layout, so the three states have to be tellable
 * apart: a grid waits through LOADING, but waiting on NOT_REQUESTED would hang
 * forever because nothing is coming.
 *
 * A failed or timed-out fetch also lands on LOADED, carrying empty preferences.
 * Losing a saved layout costs the user some column dragging; blocking the grid
 * on a request that is never going to succeed costs them the page.
 */
export const GRID_PREFERENCES_STATUS = Object.freeze({
  NOT_REQUESTED: "notRequested",
  LOADING: "loading",
  LOADED: "loaded",
});
